import React, { useState } from "react";
import { ICreateSalesData, ItemInput } from "../@types";

interface InvoiceItemErrors {
  [key: string]: string | undefined;
}

export type InvoiceErrors = {
  [P in keyof ICreateSalesData]?: string;
} & {
  items?: InvoiceItemErrors;
};

export const validateQuoteBasicInfo = (
  formData: ICreateSalesData,
  setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>
): boolean => {
  let isError = false;
  const errors: InvoiceErrors = {};

  const validateField = (field: keyof ICreateSalesData, message: string) => {
    if (!formData[field]) {
      errors[field] = message;
      isError = true;
    } else {
      delete errors[field];
    }
  };

  validateField("customerId", "Please enter customers name");
  validateField("issueDate", "Issue Date is required");
  validateField("expiryDate", "Select a due date");

  const issueDate = formData.issueDate ? new Date(formData.issueDate) : null;
  const expiryDate = formData.expiryDate ? new Date(formData.expiryDate) : null;

  if (issueDate && expiryDate) {
    if (expiryDate < issueDate) {
      errors.expiryDate = "Expiry Date cannot be less than the Issue Date";
      isError = true;
    } else {
      delete errors.expiryDate;
    }
  }

  setValidationResults(errors as InvoiceErrors);
  setValidationResults((prevErrors) => {
    return { ...prevErrors, ...errors };
  });
  return !isError;
};

export const validateInventoryItem = (
  item: ItemInput,
  index: number,
  errors: InvoiceErrors
): boolean => {
  let isValid = true;
  const variationErrors: string[] = [];
  
  // Check variations first
  if (item.variations && item.variations.length > 0) {
    item.variations.forEach((variation, variationIndex) => {
      if (!variation.price || variation.price <= 0) {
        variationErrors.push(`Variation ${variationIndex + 1} needs valid price`);
        isValid = false;
      }
      if (!variation.quantity || variation.quantity <= 0) {
        variationErrors.push(`Variation ${variationIndex + 1} needs valid quantity`);
        isValid = false;
      }
    });
    
    // If variations exist but none are valid
    if (variationErrors.length > 0) {
      errors.inventories += ` Item ${index + 1}: ${variationErrors.join(', ')}.`;
    }
  } else {
    // Validate parent item only if no variations
    if (!item.rate || item.rate <= 0) {
      errors.inventories += ` Item ${index + 1} needs valid rate.`;
      isValid = false;
    }
    if (!item.quantity || item.quantity <= 0) {
      errors.inventories += ` Item ${index + 1} needs valid quantity.`;
      isValid = false;
    }
  }

  // Common validations
  if (!item.id) {
    errors.inventories += ` Item ${index + 1} needs product ID.`;
    isValid = false;
  }
  if (!item.name) {
    errors.inventories += ` Item ${index + 1} needs product name.`;
    isValid = false;
  }

  return isValid;
};

export const validateQuoteItemDetails = (
  formData: ICreateSalesData,
  setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>,
  isRetail?: boolean
): boolean => {
  const errors: InvoiceErrors = { inventories: "", services: "" };
  let hasValidItems = false;

  if (isRetail) {
    if (!formData.inventories || formData.inventories.length === 0) {
      errors.inventories = "Please add at least one item to your quote";
    } else {
      formData.inventories.forEach((item: ItemInput, index: number) => {
        const isValid = validateInventoryItem(item, index, errors);
        if (isValid) hasValidItems = true;
        
        if (item.stockStatus === "out-of-stock") {
          errors.inventories += ` Item ${index + 1} is out of stock.`;
        }
      });
      
      if (!hasValidItems) {
        errors.inventories = "No valid inventory items found. Check prices/quantities.";
      }
    }
  } else {
    // Validate services
    if (!formData.services || formData.services.length === 0) {
      errors.services = "Add at least one service";
    } else {
      formData.services.forEach((service: ItemInput, index: number) => {
        if (!validateServiceItem(service, index, errors)) {
          errors.services += ` Service ${index + 1} has invalid details.`;
        } else {
          hasValidItems = true;
        }
      });
      
      if (!hasValidItems) {
        errors.services = "No valid service items found. Check prices/durations.";
      }
    }
  }

  setValidationResults(errors);
  return Object.values(errors).every(error => !error);
};

export const validateServiceItem = (
  service: ItemInput,
  index: number,
  errors: InvoiceErrors
): boolean => {
  ["id", "duration", "price"].forEach((field) => {
    if (!service[field]) {
      errors.services += ` Service ${index + 1} is missing ${field}.`;
      return false;
    }
  });

  return true;
};

// export const validateQuoteItemDetails = (
//   formData: ICreateSalesData,
//   setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>,
//   isRetail?: boolean
// ): boolean => {
//   const errors: InvoiceErrors = { inventories: "", services: "" };

//   if (isRetail) {
//     if (
//       !formData.inventories ||
//       formData.inventories.length === 0 ||
//       !formData.inventories[0].id
//     ) {
//       errors.inventories = "Please add at least one item to your quote";
//     } else {
//       formData.inventories.forEach((item: ItemInput, index: number) => {
//         if (!item.id) {
//           errors.inventories = "One or more inventories are out of stock";
//         } else {
//           if (item.stockStatus === "out-of-stock") {
//             errors.inventories = "One or more inventories are out of stock";
//           }
//           const inventoryValidationResult = validateInventoryItem(
//             item,
//             index,
//             errors
//           );
//           if (!inventoryValidationResult) {
//             errors.inventories = "Inventory is missing quantity";
//           }
//         }
//       });
//     }
//   } else {
//     // Validate services
//     if (
//       !formData.services ||
//       formData.services.length === 0 ||
//       !formData.services[0].id
//     ) {
//       errors.services = "Add at least one service";
//     } else {
//       formData.services.forEach((service: ItemInput, index: number) => {
//         if (!validateServiceItem(service, index, errors)) {
//           // If there is an error in service validation, set the main error
//           errors.services = "One or more services have validation errors";
//         }
//       });
//     }
//   }

//   setValidationResults(errors as InvoiceErrors);

//   // Return true if there are no errors, otherwise, return false
//   return Object.values(errors).every((error) => !error);
// };

export const validateQuoteSummary = (
  formData: ICreateSalesData,
  setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>
): boolean => {
  let isError = false;
  const errors: InvoiceErrors = {};

  const validateField = (field: keyof ICreateSalesData, message: string) => {
    if (!formData[field]) {
      errors[field] = message;
      isError = true;
    } else {
      delete errors[field];
    }
  };

  setValidationResults(errors as InvoiceErrors);
  return !isError;
};
